import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useSubscriptionInfo, useStartCheckout } from '@/hooks/useBilling';
import { cn } from '@/lib/utils';

interface PlanDef {
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  description: string;
  features: string[];
  highlight?: boolean;
}

const PLANS: PlanDef[] = [
  {
    name: 'Entry',
    monthlyPrice: 45,
    annualPrice: 460,
    description: 'For small businesses getting started with modern POS',
    features: [
      '1 location included',
      '1 register included',
      'Inventory management',
      'Customer profiles',
      'Product variants & discounts',
      'Barcode scanning',
      'Purchase orders',
      'Basic analytics & reports',
      'Staff permissions',
      'Email support',
    ],
  },
  {
    name: 'Core',
    monthlyPrice: 75,
    annualPrice: 720,
    description: 'For growing businesses that need deeper tools',
    highlight: true,
    features: [
      'Everything in Entry, plus:',
      'Multi-location support',
      'Advanced inventory',
      'Serial number tracking',
      'Work order management',
      'Advanced reporting & dashboards',
      'Accounting integrations',
      'E-commerce integration',
      'API access (limited)',
      'Phone + email support',
      'Custom staff roles',
    ],
  },
  {
    name: 'Pro',
    monthlyPrice: 145,
    annualPrice: 1110,
    description: 'For multi-location operations at scale',
    features: [
      'Everything in Core, plus:',
      'Unlimited locations',
      'Unlimited registers',
      'Full API access',
      'Multi-store transfers',
      'Advanced customer insights',
      'Loyalty program',
      'Gift cards (physical + digital)',
      'Custom fields',
      'Premium reports',
      'Priority support',
      'Personalized onboarding',
    ],
  },
];

export default function Plans() {
  const navigate = useNavigate();
  const { data: subscription } = useSubscriptionInfo();
  const startCheckout = useStartCheckout();
  const [annual, setAnnual] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  const currentPlan = subscription?.plan_name || 'Core';
  const isTrialing = subscription?.status === 'trialing';

  const handleSelectPlan = async (planName: string) => {
    setLoadingPlan(planName);
    try {
      await startCheckout.mutateAsync({
        planName,
        billingInterval: annual ? 'annual' : 'monthly',
      });
    } catch {
      setLoadingPlan(null);
    }
  };

  const formatPrice = (plan: PlanDef) => {
    if (annual) {
      const monthly = Math.round(plan.annualPrice / 12);
      return { amount: monthly, period: '/mo', note: `$${plan.annualPrice}/year (billed annually)` };
    }
    return { amount: plan.monthlyPrice, period: '/mo', note: 'Billed monthly' };
  };

  return (
    <div className="min-h-screen bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900 dark:text-white">
              Choose Your Plan
            </h1>
            {isTrialing && (
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                Your trial is on the {currentPlan} plan. Subscribe to keep access after it ends.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Billing toggle */}
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-4">
        <div className="flex items-center justify-center gap-3">
          <Label htmlFor="billing-toggle" className={cn('text-sm', !annual && 'font-semibold text-zinc-900 dark:text-white')}>
            Monthly
          </Label>
          <Switch
            id="billing-toggle"
            checked={annual}
            onCheckedChange={setAnnual}
          />
          <Label htmlFor="billing-toggle" className={cn('text-sm', annual && 'font-semibold text-zinc-900 dark:text-white')}>
            Annual
          </Label>
          {annual && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-400 text-xs">
              Save ~20%
            </Badge>
          )}
        </div>
      </div>

      {/* Plan cards */}
      <div className="max-w-6xl mx-auto px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PLANS.map((plan) => {
            const price = formatPrice(plan);
            const isCurrent = currentPlan === plan.name;
            const isLoading = loadingPlan === plan.name;

            return (
              <div
                key={plan.name}
                className={cn(
                  'relative rounded-2xl border bg-white dark:bg-zinc-900 p-6 flex flex-col',
                  plan.highlight
                    ? 'border-primary shadow-lg shadow-primary/10 ring-1 ring-primary/20'
                    : 'border-zinc-200 dark:border-zinc-700'
                )}
              >
                {plan.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-primary text-primary-foreground text-xs px-3">
                      Most Popular
                    </Badge>
                  </div>
                )}

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-zinc-900 dark:text-white mb-1">
                    {plan.name}
                  </h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    {plan.description}
                  </p>
                </div>

                <div className="mb-6">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-zinc-900 dark:text-white">
                      ${price.amount}
                    </span>
                    <span className="text-zinc-500 dark:text-zinc-400 text-sm">
                      {price.period}
                    </span>
                  </div>
                  <p className="text-xs text-zinc-400 dark:text-zinc-500 mt-1">
                    {price.note}
                  </p>
                </div>

                <ul className="space-y-2.5 mb-8 flex-1">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-600 dark:text-zinc-400">
                      {feature.endsWith(':') ? (
                        <span className="font-medium text-zinc-700 dark:text-zinc-300">{feature}</span>
                      ) : (
                        <>
                          <Check className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </>
                      )}
                    </li>
                  ))}
                </ul>

                <Button
                  onClick={() => handleSelectPlan(plan.name)}
                  disabled={isLoading || startCheckout.isPending}
                  variant={plan.highlight ? 'default' : 'outline'}
                  className="w-full"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Redirecting...
                    </>
                  ) : isCurrent && isTrialing ? (
                    `Subscribe to ${plan.name}`
                  ) : isCurrent ? (
                    'Current Plan'
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </Button>
              </div>
            );
          })}
        </div>

        <p className="text-center text-xs text-zinc-400 dark:text-zinc-500 mt-8">
          All plans include a 14-day free trial. Cancel anytime. Prices in USD.
          Additional locations and registers available on Entry and Core plans.
        </p>
      </div>
    </div>
  );
}
